import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { SegmentedFilter } from "@/components/ui/Toolbar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Icon } from "@/components/ui/Icon";
import { downloadFile, uploadFile } from "@/lib/api/files";
import { qty } from "@/lib/format";

/**
 * Excel orqali prixod / vozvrat (buyurtmachi, 2026-11-01).
 *
 * Namuna → tekshiruv (hech narsa yozilmaydi) → yozish. Qoidalar TSD
 * dagidek: tovarli qatorda LP va katak majburiy, LP bo'sh bo'lishi
 * kerak, topilmagan shtrix-kod «noma'lum» satr bo'ladi, bitta palletdagi
 * bir xil tovar qo'shiladi. Bitta xato qator butun faylni rad etadi.
 * Yozilgach oddiy sessiya: tahrir, yopish, Excel hisobot, 1C nakladnoy.
 * Bir xil faylni qayta yuklash yangi sessiya ochmaydi.
 */

type SessionKind = "RECEIPT" | "RETURN";

interface ImportLine {
  sku_id?: string;
  barcode?: string;
  article?: string;
  qty: string;
  unknown: boolean;
  rows: number[];
}

interface ImportPallet {
  lp_code: string;
  cell_code: string;
  lines: ImportLine[];
}

interface ImportDefect {
  row_number: number;
  barcode?: string;
  article?: string;
  qty: string;
  reason: string;
}

interface ImportError {
  row_number: number;
  field?: string;
  value?: string;
  error_code: string;
  message: string;
}

interface ImportResult {
  status: "VALIDATED" | "REJECTED" | "COMMITTED" | "REPLAYED";
  source_file: string;
  total_rows: number;
  unknown_lines: number;
  pallets: ImportPallet[];
  defects: ImportDefect[];
  errors: ImportError[];
  session?: { id: string; session_number: string; status: string; kind: SessionKind };
  close_error?: string;
}

interface PreviewRow extends ImportLine {
  key: string;
  lp_code: string;
  cell_code: string;
}

const STATUS_TONE: Record<ImportResult["status"], "success" | "error" | "warning" | "neutral"> = {
  VALIDATED: "success",
  REJECTED: "error",
  COMMITTED: "success",
  REPLAYED: "warning",
};

export function SessionImport() {
  const navigate = useNavigate();
  const [kind, setKind] = useState<SessionKind>("RECEIPT");
  const [zone, setZone] = useState("RECEIPT");
  const [closeAfter, setCloseAfter] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const reset = () => {
    setResult(null);
    setFailure(null);
  };

  const run = async (commit: boolean) => {
    if (!file) return;
    setBusy(true);
    reset();
    try {
      const outcome = await uploadFile<ImportResult>("inbound", "/api/v1/receiving-sessions/imports/file", file, {
        kind,
        target_zone: kind === "RETURN" ? "RETURN" : zone,
        commit: String(commit),
        close: String(commit && closeAfter),
      });
      setResult(outcome);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  const preview: PreviewRow[] = (result?.pallets ?? []).flatMap((pallet) =>
    pallet.lines.map((line, index) => ({
      ...line,
      key: `${pallet.lp_code}-${index}`,
      lp_code: pallet.lp_code,
      cell_code: pallet.cell_code,
    })),
  );
  const previewColumns: Array<Column<PreviewRow>> = [
    { key: "lp", header: "Pallet", render: (row) => <span className="mono">{row.lp_code}</span> },
    { key: "cell", header: "Katak", render: (row) => <span className="mono">{row.cell_code}</span> },
    {
      key: "sku",
      header: "Tovar",
      render: (row) =>
        row.unknown ? (
          <StatusBadge value={`noma'lum: ${row.barcode}`} tone="warning" />
        ) : (
          <span className="mono">{row.barcode || row.article}</span>
        ),
    },
    { key: "qty", header: "Miqdor", align: "right", render: (row) => qty(row.qty) },
    {
      key: "rows",
      header: "Qatorlar",
      render: (row) => (
        <span className="muted">
          {row.rows.join(", ")}
          {row.rows.length > 1 ? " · qo'shildi" : ""}
        </span>
      ),
    },
  ];
  const errorColumns: Array<Column<ImportError>> = [
    { key: "row", header: "Qator", align: "right", render: (row) => row.row_number },
    { key: "field", header: "Ustun", render: (row) => <span className="mono">{row.field || "—"}</span> },
    { key: "value", header: "Qiymat", render: (row) => <span className="mono">{row.value || "—"}</span> },
    { key: "code", header: "Xato", render: (row) => <StatusBadge value={row.error_code} tone="error" /> },
    { key: "message", header: "Sabab", render: (row) => row.message },
  ];

  const validated = result?.status === "VALIDATED";
  const label = kind === "RETURN" ? "Vozvrat" : "Prixod";

  return (
    <>
      <section className="card" style={{ padding: 20, display: "grid", gap: 14 }}>
        <div>
          <div style={{ fontWeight: 600 }}>Excel orqali prixod / vozvrat</div>
          <div className="field-hint">
            Ustunlar: Pallet (LP kodi)*, Katak*, Shtrixkod yoki Artikul*, Miqdor*, Brak miqdori, Brak sababi, Izoh.
            Katak palletning birinchi qatorida yetarli.
          </div>
          <div className="field-hint">
            LP bo'sh bo'lishi va palletdagi yorliq bilan bir xil bo'lishi kerak. Katak — prixodda qabul zonasi yoki
            bojxona, vozvratda VZ. Topilmagan shtrix-kod «noma'lum» satr bo'ladi; bitta palletdagi bir xil tovar
            qo'shiladi; faqat brak qatori palletsiz (sabab majburiy). Bir xil fayl qayta yuklansa yangi sessiya
            ochilmaydi.
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <SegmentedFilter
            value={kind}
            onChange={(value) => {
              setKind(value);
              reset();
            }}
            options={[
              { value: "RECEIPT", label: "Prixod" },
              { value: "RETURN", label: "Vozvrat" },
            ]}
          />
          {kind === "RECEIPT" && (
            <SegmentedFilter
              value={zone}
              onChange={(value) => {
                setZone(value);
                reset();
              }}
              options={[
                { value: "RECEIPT", label: "Qabul zonasi" },
                { value: "CUSTOMS", label: "Bojxona" },
              ]}
            />
          )}
          <button
            type="button"
            className="btn btn-tonal btn-sm"
            onClick={() =>
              void downloadFile(
                "inbound",
                `/api/v1/receiving-sessions/imports/template.xlsx?kind=${kind}`,
                "qabul-import-namuna.xlsx",
              ).catch((cause: unknown) => setFailure(String(cause)))
            }
          >
            <Icon name="download" size={14} />
            Namuna XLSX
          </button>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input
            ref={input}
            type="file"
            accept=".xlsx,.csv"
            aria-label="Import fayli"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              reset();
            }}
          />
          <button type="button" className="btn btn-tonal" disabled={!file || busy} onClick={() => void run(false)}>
            <Icon name="check" size={16} />
            {busy ? "Tekshirilmoqda…" : "Tekshirish"}
          </button>
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={closeAfter} onChange={(event) => setCloseAfter(event.target.checked)} />
            Import qilib darhol yopish
          </label>
          <button
            type="button"
            className="btn btn-filled"
            disabled={!validated || busy}
            title={validated ? undefined : "Avval fayl tekshiruvdan o'tishi kerak"}
            onClick={() => setConfirming(true)}
          >
            <Icon name="integration" size={16} />
            Yozish
          </button>
        </div>

        {failure && (
          <div className="notice" role="alert">
            <Icon name="alert" size={16} />
            <span>{failure}</span>
          </div>
        )}

        {result && (
          <div className="notice" role={result.status === "REJECTED" ? "alert" : undefined}>
            <Icon name={result.status === "REJECTED" ? "alert" : "check"} size={16} />
            <span>
              <StatusBadge value={result.status} tone={STATUS_TONE[result.status]} /> {result.source_file} —{" "}
              {result.total_rows} qator
              {result.status === "REJECTED" ? `, ${result.errors.length} ta xato. Hech narsa yozilmadi.` : ""}
              {result.status === "VALIDATED"
                ? `: ${result.pallets.length} pallet, ${preview.length} satr, brak ${result.defects.length}` +
                  (result.unknown_lines ? `, noma'lum ${result.unknown_lines}` : "") +
                  ". Xato yo'q — endi «Yozish» mumkin."
                : ""}
              {result.status === "REPLAYED" ? " · bu fayl avval yozilgan — yangi sessiya ochilmadi" : ""}
              {result.session ? (
                <>
                  {" "}
                  · sessiya <b className="mono">{result.session.session_number}</b> ({result.session.status})
                </>
              ) : null}
              {result.close_error ? ` · yopilmadi: ${result.close_error}` : ""}
            </span>
            {result.session && (
              <button
                type="button"
                className="btn btn-text btn-sm"
                onClick={() => navigate(result.session!.kind === "RETURN" ? "/return-sessions" : "/receiving-sessions")}
              >
                Sessiyalarga o'tish
              </button>
            )}
            {result.status === "REJECTED" && (
              <button
                type="button"
                className="btn btn-text btn-sm"
                onClick={() =>
                  void downloadFile("inbound", "/api/v1/receiving-sessions/imports/errors.xlsx", "qabul-import-xatolar.xlsx", {
                    errors: result.errors,
                  }).catch((cause: unknown) => setFailure(String(cause)))
                }
              >
                <Icon name="report" size={14} />
                Xatolar XLSX
              </button>
            )}
          </div>
        )}
      </section>

      {result?.status === "REJECTED" && (
        <section style={{ marginTop: 16 }}>
          <DataTable
            columns={errorColumns}
            rows={result.errors}
            rowKey={(row, index) => `${row.row_number}-${row.field}-${index}`}
            emptyTitle="Qator xatosi yo'q"
          />
        </section>
      )}

      {result && result.status !== "REJECTED" && preview.length > 0 && (
        <section style={{ marginTop: 16 }}>
          <DataTable columns={previewColumns} rows={preview} rowKey={(row) => row.key} emptyTitle="Satr yo'q" />
        </section>
      )}

      {result && result.status !== "REJECTED" && result.defects.length > 0 && (
        <section style={{ marginTop: 16 }}>
          <DataTable
            columns={[
              { key: "row", header: "Qator", align: "right", render: (row: ImportDefect) => row.row_number },
              {
                key: "sku",
                header: "Brak tovar",
                render: (row: ImportDefect) => <span className="mono">{row.barcode || row.article}</span>,
              },
              { key: "qty", header: "Miqdor", align: "right", render: (row: ImportDefect) => qty(row.qty) },
              { key: "reason", header: "Sabab", render: (row: ImportDefect) => row.reason },
            ]}
            rows={result.defects}
            rowKey={(row) => String(row.row_number)}
            emptyTitle="Brak yo'q"
          />
        </section>
      )}

      <ConfirmDialog
        open={confirming}
        title={`${label}: Excel dan sessiya yaratish`}
        description={
          <>
            <b>{file?.name}</b> tekshiruvdan o'tdi ({result?.pallets.length ?? 0} pallet). Sessiya TSD dagidek
            yaratiladi{closeAfter ? " va darhol yopiladi — qoldiq yoziladi" : "; keyin uni yopish kerak"}.
          </>
        }
        confirmLabel="Yozish"
        busy={busy}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          void run(true);
        }}
      />
    </>
  );
}
